import type { LayoutPoint, ViewerPosition } from '../contracts/models';

export type PolygonPointInput = readonly number[];

/**
 * Removes invalid vertices and the optional repeated closing point used by
 * some backend payloads. SVG closes polygons implicitly.
 */
export const normalizeLayoutPolygon = (
  points: readonly PolygonPointInput[],
): LayoutPoint[] => {
  if (!Array.isArray(points) || points.length === 0) return [];

  const normalized = points
    .filter(
      (point) =>
        Array.isArray(point) &&
        point.length === 2 &&
        Number.isFinite(point[0]) &&
        Number.isFinite(point[1]),
    )
    .map((point): LayoutPoint => [point[0], point[1]]);

  if (normalized.length > 1) {
    const [firstX, firstY] = normalized[0];
    const [lastX, lastY] = normalized[normalized.length - 1];
    if (firstX === lastX && firstY === lastY) {
      return normalized.slice(0, -1);
    }
  }

  return normalized;
};

/**
 * Calculates the signed-area centroid, falling back to the mean for a line or
 * degenerate polygon.
 */
export const calculatePolygonCentroid = (
  points: readonly PolygonPointInput[],
): ViewerPosition => {
  if (points.length === 0) return { x: 0, y: 0 };

  if (points.length < 3) {
    return {
      x: points.reduce((sum, point) => sum + point[0], 0) / points.length,
      y: points.reduce((sum, point) => sum + point[1], 0) / points.length,
    };
  }

  let area = 0;
  let centroidX = 0;
  let centroidY = 0;

  for (let index = 0; index < points.length; index += 1) {
    const nextIndex = (index + 1) % points.length;
    const [x, y] = points[index];
    const [nextX, nextY] = points[nextIndex];
    const cross = x * nextY - nextX * y;

    area += cross;
    centroidX += (x + nextX) * cross;
    centroidY += (y + nextY) * cross;
  }

  area *= 0.5;

  if (Math.abs(area) < 0.001) {
    return {
      x: points.reduce((sum, point) => sum + point[0], 0) / points.length,
      y: points.reduce((sum, point) => sum + point[1], 0) / points.length,
    };
  }

  return {
    x: centroidX / (6 * area),
    y: centroidY / (6 * area),
  };
};
